import { ScreenInit } from './components/Player/ScreenManager'
import { Screen } from 'common/types'
import { isNewScreen, reload } from './utils'
import { mutate } from 'swr'

const API_BASE_URL = '/api/'

export class HTTPError extends Error {
  constructor(
    public statusCode: number,
    public message: string
  ) {
    super(message || statusCode.toString())
  }
}

export const resolveUrl = (path: string, queryParams?: Record<string, any>) => {
  const base = `${API_BASE_URL}${path}`
  const paramsStr = new URLSearchParams(queryParams).toString()
  return paramsStr ? `${base}?${paramsStr}` : base
}

export const fetcher = (...args: Parameters<typeof fetch>) =>
  fetch(...args).then(async (res) => {
    const isJson = res.headers.get('content-type')?.includes('application/json')
    const data = isJson ? await res.json() : null

    // check for error response
    if (!res.ok) {
      // get error message from body or default to response status
      const error = new HTTPError(res.status, data?.message || res.statusText)
      throw error
    }
    return data
  })

export function postJsonData(url: string, body: Record<string, any>) {
  return fetcher(url, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

export function registerScreen(screen: Screen | ScreenInit) {
  if (isNewScreen(screen)) {
    return postJsonData(resolveUrl('register-screen/'), screen)
  }

  const { id } = screen
  return postJsonData(resolveUrl(`register-screen/${id}/`), {})
}

interface HealthPingResponse {
  localPlayback: boolean
}

export function sendHealthPing(screenId: string): Promise<HealthPingResponse> {
  return postJsonData(resolveUrl(`screen/${screenId}/health`), {})
}

// SSE connection management
let evtSource: EventSource | undefined
let currentScreenId: string | undefined

function refresh() {
  if (evtSource && evtSource.readyState === EventSource.OPEN) {
    evtSource.close()
  }
  reload()
}

export function ensureSSE(): void {
  if (evtSource?.readyState === EventSource.OPEN) {
    return
  }

  if (!currentScreenId) {
    console.warn('Cannot reconnect SSE: no screen ID')
    return
  }

  console.log('SSE: trying to reconnect...')

  // Close existing connection
  if (evtSource) {
    evtSource.close()
    evtSource = undefined
  }

  // Reinitialize
  initSSE(currentScreenId)
}

/**
 * Initialize SSE connection for screen events.
 * Will skip if already connected or connecting.
 */
export function initSSE(id: string) {
  // Allow reconnection if closed or if ID changed
  if (evtSource && evtSource.readyState !== EventSource.CLOSED && currentScreenId === id) {
    return
  }

  // Close existing if changing screens
  if (evtSource && currentScreenId !== id) {
    evtSource.close()
    evtSource = undefined
  }

  currentScreenId = id
  const endpoint = resolveUrl(`screen/${id}/sse`)
  evtSource = new EventSource(endpoint)

  evtSource.addEventListener('cast', (event) => {
    console.log('Cast event', event)
    refresh()
  })

  evtSource.addEventListener('screen-update', (event) => {
    console.log('Screen update event', event)
    refresh()
  })

  evtSource.addEventListener('pair', (event) => {
    console.log('Pair event', event)
    refresh()
  })

  evtSource.addEventListener('remove', (event) => {
    console.log('Remove event', event)
    refresh()
  })

  evtSource.addEventListener('screen-settings-update', (event) => {
    console.log('Screen settings update event', event)
    refresh()
  })

  evtSource.addEventListener('reload', (event) => {
    console.log('Reload event', event)
    refresh()
  })

  evtSource.onerror = (err) => {
    const state = evtSource?.readyState
    console.error(`SSE error (readyState: ${state}):`, err)
  }

  evtSource.onopen = () => {
    console.log('SSE: connected')
  }
}
